import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Notify, toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useLines } from "@/features/lines/hooks";
import { useProducts } from "@/features/products/hooks";
import type { IndicatorDefinitionResponse } from "@/types/indicators.types";
import { IndicatorList } from "./IndicatorList";
import { IndicatorEditor } from "./IndicatorEditor";
import { useDeleteIndicator, useIndicators, useIndicatorPreview, useSaveIndicator } from "./hooks";
import { useIndicatorDraft } from "./useIndicatorDraft";

/**
 * Spec 082 — os indicadores próprios da organização. Orquestrador: queries e composição, com a
 * mecânica do rascunho em `useIndicatorDraft`.
 */
export function IndicatorsSection({ canEdit }: { canEdit: boolean }) {
  const query = useIndicators();
  const { data: lines = [] } = useLines();
  const { data: products = [] } = useProducts();
  const save = useSaveIndicator();
  const remove = useDeleteIndicator();
  const preview = useIndicatorPreview();
  const [editing, setEditing] = useState(false);
  const { draft, setDraft, load, reset, value, isCalculating } = useIndicatorDraft(preview);

  const lineOptions = useMemo(() => lines.map((l) => ({ id: l.id, name: l.name })), [lines]);
  // Enquanto não há leitura de "o que esta linha registra", a lista é a de produtos da
  // organização. A restrição por linha e sentido (FR-028) entra quando essa leitura existir.
  const productsFor = useMemo(
    () => () => products.map((p) => ({ id: p.id, name: p.name })),
    [products]
  );

  function novo() {
    reset();
    setEditing(true);
  }

  function editar(item: IndicatorDefinitionResponse) {
    load(item);
    setEditing(true);
  }

  async function salvar() {
    try {
      await save.mutateAsync({
        id: draft.id,
        payload: {
          name: draft.name.trim(),
          baseUnit: draft.baseUnit.trim(),
          target: draft.target ?? null,
          terms: draft.terms.filter((t) => t.lineId && t.productId),
        },
      });
      toast.success("Indicador salvo.");
      setEditing(false);
      reset();
    } catch (err) {
      toast.error("Não foi possível salvar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  async function apagar(item: IndicatorDefinitionResponse) {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Apagar "${item.name}"?`,
      description: "O indicador deixa de aparecer nas telas que o usam.",
      confirmLabel: "Apagar indicador",
      typeToConfirm: item.name,
    });
    if (!ok) return;
    try {
      await remove.mutateAsync(item.id);
      toast.success("Indicador apagado.");
    } catch (err) {
      toast.error("Não foi possível apagar.", { description: getFriendlyErrorMessage(err) });
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base">Indicadores próprios</CardTitle>
          <p className="text-xs text-muted-foreground">
            Montados por frases, sobre o que as linhas registram. Ficam fora do OEE.
          </p>
        </div>
        {canEdit && !editing ? (
          <Button size="sm" onClick={novo}>
            Novo indicador
          </Button>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        {query.isError ? (
          <Notify.QueryError error={query.error} onRetry={() => void query.refetch()} />
        ) : editing ? (
          <IndicatorEditor
            draft={draft}
            setDraft={setDraft}
            lines={lineOptions}
            productsFor={productsFor}
            value={value}
            isCalculating={isCalculating}
            isSaving={save.isPending}
            onSave={() => void salvar()}
            onCancel={() => {
              setEditing(false);
              reset();
            }}
          />
        ) : (
          <IndicatorList
            items={query.data ?? []}
            canEdit={canEdit}
            onEdit={editar}
            onDelete={(i) => void apagar(i)}
            onNew={novo}
          />
        )}
      </CardContent>
    </Card>
  );
}
